import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import * as _ from 'lodash';
import {Observable, OperatorFunction} from 'rxjs';
import {ConfigurationService} from '../../../../entry-routes/configuration/configuration.service';
import {Aggregate} from '../../../models/aggregate';
import {AggregateOptions} from '../../../models/aggregate-request-params';
import {CdxDocument} from '../../../models/cdx-document';
import {EsPage} from '../../../models/es-page';
import {InternalRoutes} from '../../../models/internal-routes';
import {Metadata} from '../../../models/metadata';
import {ObjectType} from '../../../models/ObjectType';
import {SearchItem} from '../../../models/search-item';
import {SearchPath} from '../../../models/search-path';
import {Url} from '../../../models/url';
import {ThesaurusService} from '../../../services/thesaurus/thesaurus.service';
import {DocumentDetailsService} from '../../details/service/document/document-details.service';
import {FilterDatas, FilterState} from '../../document-search/document-filter/reducer/filter.reducer';
import {DocumentSearchResultAction} from '../../document-search/document-search-result/action/document-search-result.action';
import {
  selectDocumentSearchFilterDatas, selectDocumentSearchFilterFeature,
  selectDocumentSearchItemDatas,
  selectDocumentSearchResultDatas,
  selectDocumentSearchResultFeature
} from '../../document-search/document-search-selectors';
import {GedCfgFieldService} from '../../field/service/ged/ged-cfg-field.service';
import {AbstractFilterService} from '../../filter/service/abstract-filter.service';
import {DocumentFilterService} from '../../filter/service/document-filter.service';
import {SearchResultPaginationService} from '../../search-result-pagination/service/search-result-pagination.service';
import {AbstractSearchResultService} from './abstract-search-result.service';
import {
  DocumentSearchResultState
} from '../../document-search/document-search-result/reducer/document-search-result.reducer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from '../../../../entry-routes/authentication/authentication.service'

@Injectable({
  providedIn: 'root'
})
export class DocumentSearchResultService extends AbstractSearchResultService {

  documentSearchItems$ = this.store.select(selectDocumentSearchItemDatas);
  documentFilterDatas$ = this.store.select(selectDocumentSearchFilterDatas);
  documentSearchListResult$ = this.store.select(selectDocumentSearchResultDatas);
  private documentSearchResultState: DocumentSearchResultState;
  private filterState: FilterState;

  private filterService: AbstractFilterService;

  constructor(
    http: HttpClient,
    fieldService: GedCfgFieldService,
    thesaurusService: ThesaurusService,
    documentFilterService: DocumentFilterService,
    protected searchResultPaginationService: SearchResultPaginationService,
    private documentSearchResultAction: DocumentSearchResultAction,
    private configAction: ConfigurationService,
    private documentDetailsService: DocumentDetailsService,
    store: Store,
    authenticationService: AuthenticationService
  ) {
    super(
      http,
      fieldService,
      thesaurusService,
      store,
      authenticationService
    );
    this.documentDetailsService.setDocumentSearchResultService(this);
    this.thesaurusService.setDocumentSearchResultService(this);
    this.filterService = documentFilterService;

    this.store.select(selectDocumentSearchResultFeature)
      .pipe(takeUntilDestroyed())
      .subscribe( documentSearchResultState => this.documentSearchResultState = documentSearchResultState);

    this.store.select(selectDocumentSearchFilterFeature)
      .pipe(takeUntilDestroyed())
      .subscribe(filterState => this.filterState = filterState );

  }

  public getServiceType(): InternalRoutes {
    return InternalRoutes.DOCUMENTS;
  }

  public getObjectType(): ObjectType {
    return ObjectType.DOCUMENT;
  }

  public getSearchItem$(): Observable<SearchItem[]> {
    return this.documentSearchItems$;
  }

  public getFilterDatas$(): Observable<FilterDatas> {
    return this.documentFilterDatas$;
  }

  public getSearchListResult$(): Observable<EsPage<CdxDocument>> {
    return this.documentSearchListResult$;
  }

  public getSearchBaseUrl(): string {
    return Url.getProtectedApiBaseUrl(this.configAction) + Url.DOCUMENTS + Url.SEARCH;
  }

  public getAggBaseUrl(): string {
    return Url.getProtectedApiBaseUrl(this.configAction) + Url.DOCUMENTS + Url.AGGS;
  }

  public getEmptyEspage(): EsPage<CdxDocument> {
    return new EsPage<CdxDocument>(0, [], 0, 0, [], false);
  }

  public searchDrafts(pageNumber, keepLastAgg): void {
    console.log('document-search-result-servcice searchDrafts() pageNumber:'+pageNumber+' keepLastAgg:'+keepLastAgg)
    return;
  }


  public search(): void {
    // check for lastAgg if exists for cdx_type
    // this.filterService.removeAllFilters();
    if (!this.filterState || !this.filterState.datas || !this.filterState.datas.lastAgg) {
      this.filterService.removeAllFilters();
    } else {
      const searchPath: SearchPath = new SearchPath(this.filterState.datas.lastAgg.path);
      if (searchPath.code !== SearchPath.META_CDX_TYPE) {
        this.filterService.removeAllFilters();
      } else {
        this.filterService.removeFiltersAndKeepLastAgg();
      }
    }

    this.documentDetailsService.remove();
    this.documentSearchResultAction.loadDocumentsStarted();
    try {
      super._search().subscribe((page: EsPage<CdxDocument>) => {
          // load unknown fields
          this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
          if (!!this.filterState && !!this.filterState.datas && !!this.filterState.datas.lastAgg) {
            const lastAggSearchPath: SearchPath = new SearchPath(this.filterState.datas.lastAgg.path);
            if (lastAggSearchPath.isMeta && lastAggSearchPath.code === SearchPath.META_CDX_TYPE) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.filterState.datas.lastAgg.path) {
                  return this.filterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            this.filterService.removeAllFilters();
          }

          //On enlève les aggregats concernant les équipes pour pouvoir les ajouter sur demande
          page.aggs = page.aggs.filter((aggregate: Aggregate) => {
              return aggregate.path !== SearchPath.META_CDX_CLASSIFICATION + SearchPath.PATH_PART_SEPARATOR + SearchPath.META_CDX_TEAM;
            });

          // send result to store
          this.documentSearchResultAction.loadDocumentsSucceeded(page as EsPage<CdxDocument>);
        },
        (error: HttpErrorResponse) => {
          this.documentSearchResultAction.loadDocumentsFailed(error);

          // si plantage cela peut être du au tri, on le retire du store pour pouvoir relancer la recherche sans
          this.searchResultPaginationService.reinitSearchResultPagination();
        }
      );
    } catch (error) {
      this.documentSearchResultAction.loadDocumentsFailed(error);
    }

  }

  public getDocumentsByEntityId(entityTypeCode: string, entityId: string): Observable<EsPage<CdxDocument>> {
    return super._getDocumentsByEntityId(entityTypeCode, entityId);
  }

  public setNewPageToStore(page: EsPage<CdxDocument>): void {
    this.filterService.removeAllFilters();
    this.documentDetailsService.remove();
    this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
    // send result to store
    this.documentSearchResultAction.loadDocumentsSucceeded(page as EsPage<CdxDocument>);
  }

  public filter(keepLastAgg = false): void {
    this.documentDetailsService.remove();
    this.documentSearchResultAction.loadDocumentsStarted();

    try {
      super._filter()
        .subscribe((page: EsPage<CdxDocument>) => {
            // load unknown fields
            // this.fieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
            if (!!this.filterState.datas.lastAgg && keepLastAgg) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.filterState.datas.lastAgg.path) {
                  return this.filterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            // send result to store
            this.documentSearchResultAction.loadDocumentsSucceeded(page as EsPage<CdxDocument>);
          }, (error: HttpErrorResponse) => {
            this.documentSearchResultAction.loadDocumentsFailed(error);
          }
        );
    } catch (error) {
      this.documentSearchResultAction.loadDocumentsFailed(error);
    }
  }

  public gotoPage(pageNum: number): void {
    this.documentDetailsService.remove();
    this.documentSearchResultAction.updateDocumentsStarted();
    try {
      super._gotoPage(pageNum)
        .subscribe((page: EsPage<CdxDocument>) => {
            // load unknown fields
            this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
            if (!!this.filterState && !!this.filterState.datas && !!this.filterState.datas.lastAgg) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.filterState.datas.lastAgg.path) {
                  return this.filterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            this.documentSearchResultAction.updateDocumentsSucceeded(page as EsPage<CdxDocument>);
          }, (error: HttpErrorResponse) => {
            this.documentSearchResultAction.updateDocumentsFailed(error);
          }
        );
    } catch (error) {
      this.documentSearchResultAction.updateDocumentsFailed(error);
    }
  }

  public loadAggregate(path: string, opts: AggregateOptions, checkPath = false): void {
    this.documentSearchResultAction.loadAggregateStart();
    try {
      this._loadAggregate(path, opts, checkPath)
        .subscribe((agg: Aggregate) => {
            // this.fieldService.loadFieldsFromPath(path);
            this.documentSearchResultAction.loadAggregateSucceeded(agg);
          },
          (error: HttpErrorResponse) => {
            this.documentSearchResultAction.loadAggregateFailed(error);
          });
    } catch (error) {
      this.documentSearchResultAction.loadAggregateFailed(error);
    }
  }

  public workspaceParams(): OperatorFunction<HttpParams, HttpParams> {
    return super.workspaceParams();
  }

  public coordParams(limit: number = AbstractSearchResultService.DOCUMENT_PAGE_SIZE, offset = 0): OperatorFunction<HttpParams, HttpParams> {
    return super.coordParams(limit, offset);
  }

  public searchItemsParams(pathToCheck: any = null): OperatorFunction<HttpParams, HttpParams> {
    return super.searchItemsParams(pathToCheck);
  }

  public filterItemsParams(path: any = null): OperatorFunction<HttpParams, HttpParams> {
    return super.filterItemsParams(path);
  }


  public deleteStoreDocument(docId: string): void {
    const documentSearchListResult: EsPage<CdxDocument> = !!this.documentSearchResultState ? this.documentSearchResultState.datas : null;
    if (!documentSearchListResult) {
      return;
    }
    const cloneDocumentSearchListResult = _.cloneDeep(documentSearchListResult);
    const deletedDocument: CdxDocument = cloneDocumentSearchListResult.content.filter((document: CdxDocument) => document.cdx_id === docId)[0];
    if (!!deletedDocument) {
      cloneDocumentSearchListResult.numberOfElements -= 1;
      cloneDocumentSearchListResult.content = cloneDocumentSearchListResult.content.filter((doc: CdxDocument) => doc.cdx_id !== deletedDocument.cdx_id);
      this.documentSearchResultAction.loadDocumentsSucceeded(cloneDocumentSearchListResult);
    }
  }

  public updateDocumentInSearchResultPage(docId: string, meta: Metadata): void {
    const documentSearchListResult: EsPage<CdxDocument> = !!this.documentSearchResultState ?
      this.documentSearchResultState.datas : null;
    if (!documentSearchListResult) {
      return;
    }
    const cloneDocumentSearchListResult = _.cloneDeep(documentSearchListResult);
    const searchedDocument: CdxDocument = cloneDocumentSearchListResult.content.find((storeDoc: CdxDocument) => storeDoc.cdx_id === docId);
    if (!!searchedDocument) {
      searchedDocument.metadata = meta;
      this.documentSearchResultAction.updateDocumentSucceeded(searchedDocument);
    }
  }

  getDocumentSearchResultState(): DocumentSearchResultState {
    return this.documentSearchResultState;
  }
}
